import { env } from '../env';
import { clearAdminSession, getSessionToken } from '../auth/session';

export class AdminApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly details?: unknown;

  public constructor(params: { status: number; code: string; message: string; details?: unknown }) {
    super(params.message);
    this.name = 'AdminApiError';
    this.status = params.status;
    this.code = params.code;
    this.details = params.details;
  }
}

type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface AdminApiRequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  expectedStatus?: number | number[];
}

export interface AdminApiClient {
  request<TResponse>(path: string, options?: AdminApiRequestOptions): Promise<TResponse>;
}

export interface CreateAdminApiClientOptions {
  baseUrl?: string;
  fetcher?: typeof fetch;
  tokenProvider?: () => string | null;
  onUnauthorised?: () => void;
}

const defaultOnUnauthorised = () => {
  clearAdminSession();
  if (typeof window !== 'undefined' && window.location) {
    window.location.href = '/login';
  }
};

export const createAdminApiClient = (options: CreateAdminApiClientOptions = {}): AdminApiClient => {
  const baseUrl = options.baseUrl ?? env.VITE_API_BASE_URL;
  const fetcher = options.fetcher ?? fetch.bind(globalThis);
  const tokenProvider = options.tokenProvider ?? getSessionToken;
  const onUnauthorised = options.onUnauthorised ?? defaultOnUnauthorised;

  const request = async <TResponse>(
    path: string,
    {
      method = 'GET',
      body,
      headers: requestHeaders,
      signal,
      expectedStatus,
    }: AdminApiRequestOptions = {},
  ): Promise<TResponse> => {
    const url = path.startsWith('http') ? path : `${baseUrl}${path}`;
    const headers = new Headers(requestHeaders);
    const token = tokenProvider();
    if (token) headers.set('authorization', `Bearer ${token}`);

    let payload: BodyInit | undefined;
    if (body !== undefined) {
      if (typeof FormData !== 'undefined' && body instanceof FormData) {
        payload = body;
      } else {
        headers.set('content-type', 'application/json');
        payload = JSON.stringify(body);
      }
    }

    const response = await fetcher(url, { method, headers, body: payload, signal });
    if (response.status === 401) onUnauthorised();

    const text = await response.text();
    const parsed = text.length > 0 ? (JSON.parse(text) as unknown) : undefined;

    if (!response.ok) {
      const errorBody = (parsed ?? {}) as { code?: string; message?: string };
      throw new AdminApiError({
        status: response.status,
        code: errorBody.code ?? 'AdminApiError',
        message: errorBody.message ?? response.statusText,
        details: parsed,
      });
    }

    if (expectedStatus !== undefined) {
      const allowedStatuses = Array.isArray(expectedStatus) ? expectedStatus : [expectedStatus];
      if (!allowedStatuses.includes(response.status)) {
        throw new AdminApiError({
          status: response.status,
          code: 'UnexpectedStatusCode',
          message: `Unexpected response status ${response.status}; expected ${allowedStatuses.join(' or ')}.`,
          details: parsed,
        });
      }
    }

    return parsed as TResponse;
  };

  return { request };
};

export const adminApiClient = createAdminApiClient();
